// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import RFQRequestModal from "@conklin/App/Components/RFQRequestModal/RFQRequestModal";
import { resolveQuantity } from "@conklin/App/Hooks/xUseQty";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import IconButtonCart from "@conklin/XDL/Icons/IconButtonCart";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { parseBoolean } from "@conklin/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconMemo } from "@insite/mobius/Icon";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // XNG-Change
    isIconButton?: boolean;
    reorderLink?: boolean;
    secondaryRequestQuoteBtn?: boolean;
    isPdp?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    HasToasterContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        // XNG-Changes
        isAuthenticated: xIsAuthenticated(state),
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

// XNG-Changes
export const productAddToCartIconButtonStyles: ButtonPresentationProps = {
    sizeVariant: "small",
    css: css`
        padding: 0 12px;
    `,
};

// xng-change
const noteMessageStyle: TypographyPresentationProps = {
    forwardAs: "p",
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
    css: css`
        margin-bottom: 8px;
    `,
};

const readNearestQtyValue = (origin: HTMLElement | null) => {
    const container = origin?.closest(
        "[data-test-selector='product_actions'], [data-test-selector='product-actions-grid-template']",
    );
    if (!container) {
        return undefined;
    }

    const inputs = container.querySelectorAll<HTMLInputElement>(
        "[data-test-selector='productQty'], [data-test-selector='product_qtyOrdered']",
    );
    if (inputs.length !== 1) {
        return undefined;
    }

    return inputs[0].value;
};

const ProductAddToCartButton = ({
    productContext: {
        product: {
            id: productId,
            allowZeroPricing,
            quoteRequired,
            isVariantParent,
            properties,
            detail,
            minimumOrderQty,
        },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, failedToLoadPricing },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    // XNG-Changes
    isIconButton,
    reorderLink,
    isAuthenticated,
    secondaryRequestQuoteBtn = false,
    isPdp = false,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);
    // XNG-Changes
    const [rfqRequestModalIsOpen, setRFQRequestModalIsOpen] = useState(false);

    // XNG-Changes
    const [iconButtonStyles] = useState(() => mergeToNew(productAddToCartIconButtonStyles, extendedStyles));
    const invalidPrice =
        (!allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) || failedToLoadPricing;
    const requiresQuote =
        quoteRequired ||
        (!isAuthenticated && parseBoolean(properties.authRequiredForPurchase)) ||
        secondaryRequestQuoteBtn ||
        (!isPdp && invalidPrice);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const addToCartClickHandler = async (event?: React.MouseEvent<HTMLElement>) => {
        // The qty field keeps the typed/stepped value locally until blur. Read it here so Add to Cart
        // uses that quantity instead of the last value stored on the product.
        const visibleQty = readNearestQtyValue(event?.currentTarget ?? null);
        const resolvedVisibleQty =
            visibleQty != null && visibleQty !== ""
                ? resolveQuantity(visibleQty, Number(detail?.multipleSaleQty) || 1, Math.max(minimumOrderQty || 1, 1))
                : undefined;
        const qtyToAdd = resolvedVisibleQty || qtyOrdered;

        // XNG-Changes
        if (requiresQuote) {
            setRFQRequestModalIsOpen(true);
            return;
        }

        onAddToCartClick?.();

        // XNG-Change: requiresQuote & failedToLoadPricing
        if (
            (!requiresQuote && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) ||
            failedToLoadPricing
        ) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered: qtyToAdd,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
            // XNG-Changes
            additionalQueryStringParameters:
                secondaryRequestQuoteBtn || invalidPrice ? { lineQuoteRequested: true } : {},
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
    };

    // XNG-Changes
    if (isIconButton) {
        return (
            <Button
                {...iconButtonStyles}
                onClick={addToCartClickHandler}
                disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled || isVariantParent}
                {...otherProps}
                aria-label="add to cart"
            >
                <IconMemo src={IconButtonCart} size={16} color="common.ui1" />
            </Button>
        );
    }

    // XNG-Change
    if (reorderLink) {
        return (
            <Button
                {...iconButtonStyles}
                onClick={addToCartClickHandler}
                disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                {...otherProps}
                aria-label="add to cart icon button"
            >
                {labelOverride || translate("Reorder")}
            </Button>
        );
    }

    return (
        <>
            {/* xng-change */}
            {requiresQuote && isPdp && (
                <Typography {...noteMessageStyle}>
                    {siteMessage(
                        "Custom_Requires_Quote_Add_To_Cart_Message",
                        "Request Delivered Price for more details including freight and taxes.",
                    )}
                </Typography>
            )}
            <Button
                {...styles}
                onClick={addToCartClickHandler}
                // XNG-Change
                disabled={
                    qtyOrdered <= 0 ||
                    isAddToCartButtonDisabled ||
                    isVariantParent ||
                    (isPdp && invalidPrice && !requiresQuote && !secondaryRequestQuoteBtn)
                }
                {...otherProps}
            >
                {/* XNG-Change: requiresQuote */}
                {requiresQuote
                    ? translate(isPdp ? "Request Delivered Price" : "Request Quote")
                    : labelOverride || translate("Add to Cart")}
            </Button>

            {/* XNG-Changes */}
            <RFQRequestModal
                productId={productId}
                isModalOpened={rfqRequestModalIsOpen}
                handleCloseModal={() => setRFQRequestModalIsOpen(false)}
            />
        </>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(withToaster(ProductAddToCartButton)));
